import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import type { DocumentEntry } from "./ManualIdvDocumentEntry";

export type ManualIdvDocumentValidationProfile = "basic" | "standard_enhanced";

export type ManualIdvRouteId = "ROUTE_1" | "ROUTE_2" | "ROUTE_3";

type RouteCopy = {
  label: string;
  description: string;
};

type RouteRequirementsInfo = {
  title: string;
  items: string[];
};

export type SavedDocumentCounts = {
  saved: DocumentEntry[];
  group1Count: number;
  group2aCount: number;
  totalCount: number;
};

export type AreManualIdvRouteRequirementsMet = (
  counts: SavedDocumentCounts,
  route: string
) => boolean;

export type ManualIdvRouteRulesConfig = {
  routesIntro: string;
  availableRoutes: ManualIdvRouteId[];
  routeCopy: Record<ManualIdvRouteId, RouteCopy>;
  routeRequirementsInfo: Partial<
    Record<ManualIdvRouteId, RouteRequirementsInfo>
  >;
  areRouteRequirementsMet: AreManualIdvRouteRequirementsMet;
};

function countSavedDocuments(documents: DocumentEntry[]): SavedDocumentCounts {
  const saved = documents.filter((d) => d.saved);
  const group1Count = saved.filter(
    (d) => d.document_group === "GROUP_1"
  ).length;
  const group2aCount = saved.filter(
    (d) => d.document_group === "GROUP_2A"
  ).length;
  const totalCount = saved.length;
  return { saved, group1Count, group2aCount, totalCount };
}

const standardEnhancedAreRouteRequirementsMet: AreManualIdvRouteRequirementsMet =
  (counts, route) => {
    const { group1Count, group2aCount, totalCount, saved } = counts;

    if (route === "ROUTE_1") {
      const additionalCount = group1Count >= 1 ? totalCount - 1 : totalCount;
      return group1Count >= 1 && additionalCount >= 2;
    }
    if (route === "ROUTE_2") {
      const additionalCount = group2aCount >= 1 ? totalCount - 1 : totalCount;
      return group2aCount >= 1 && additionalCount >= 2;
    }
    if (route === "ROUTE_3") {
      const hasBirthCert = saved.some(
        (d) => d.document_type === "BIRTH_CERTIFICATE_AFTER_12_MONTHS"
      );
      return hasBirthCert && group2aCount >= 1 && totalCount >= 5;
    }
    return false;
  };

const basicAreRouteRequirementsMet: AreManualIdvRouteRequirementsMet = (
  counts,
  route
) => {
  const { group1Count, group2aCount, totalCount } = counts;

  if (route === "ROUTE_1") {
    return group1Count >= 1 && totalCount >= 2;
  }
  if (route === "ROUTE_2") {
    if (group1Count > 0) {
      return false;
    }
    return group2aCount >= 1 && totalCount >= 3;
  }
  return false;
};

const ROUTE_MATCHERS: Record<
  ManualIdvDocumentValidationProfile,
  AreManualIdvRouteRequirementsMet
> = {
  basic: basicAreRouteRequirementsMet,
  standard_enhanced: standardEnhancedAreRouteRequirementsMet,
};

const PROFILE_BY_FORM_VALIDATION: Record<
  string,
  ManualIdvDocumentValidationProfile
> = {
  basic: "basic",
  standard_enhanced: "standard_enhanced",
};

export function resolveManualIdvDocumentValidationProfile(
  formConfig: Record<string, boolean | string>
): ManualIdvDocumentValidationProfile {
  const profile = formConfig.document_validation_profile;
  if (typeof profile === "string" && profile in PROFILE_BY_FORM_VALIDATION) {
    return PROFILE_BY_FORM_VALIDATION[profile];
  }
  return "standard_enhanced";
}

export function areManualIdvRouteRequirementsMet(
  documents: DocumentEntry[],
  route: string,
  profile: ManualIdvDocumentValidationProfile
): boolean {
  return ROUTE_MATCHERS[profile](countSavedDocuments(documents), route);
}

export function useManualIdvRouteRules(): Record<
  ManualIdvDocumentValidationProfile,
  ManualIdvRouteRulesConfig
> {
  const { t } = useTranslation();

  return useMemo(() => {
    const route1Label = t("Route 1 — Primary identity documents");
    const route1RequirementsTitle = t("Route 1 requirements");
    const route2RequirementsTitle = t("Route 2 requirements");
    const oneGroup1Document = t("1 document from Group 1");
    const oneGroup2aDocument = t("1 document from Group 2a");
    const standardRoute3Copy: RouteCopy = {
      label: t("Route 3 — Alternative document route"),
      description: t(
        "Used only if Routes 1 and 2 cannot be satisfied. Requires a birth certificate and multiple supporting documents from Groups 2a and 2b."
      ),
    };

    const standardEnhanced: ManualIdvRouteRulesConfig = {
      routesIntro: t(
        "Use Route 1 wherever possible. If the applicant cannot provide a Group 1 document, use Route 2. Use Route 3 only if Route 2 requirements cannot be met."
      ),
      availableRoutes: ["ROUTE_1", "ROUTE_2", "ROUTE_3"],
      routeCopy: {
        ROUTE_1: {
          label: route1Label,
          description: t(
            "The applicant provides 1 document from Group 1 (such as a passport or driving licence) and 2 additional documents from Group 1, 2a or 2b."
          ),
        },
        ROUTE_2: {
          label: t("Route 2 — Secondary documents with external check"),
          description: t(
            "Used only if Route 1 cannot be satisfied. Requires 1 document from Group 2a, 2 additional documents, and an external identity validation check."
          ),
        },
        ROUTE_3: standardRoute3Copy,
      },
      routeRequirementsInfo: {
        ROUTE_1: {
          title: route1RequirementsTitle,
          items: [
            oneGroup1Document,
            t("2 additional documents from Group 1, 2a or 2b"),
          ],
        },
        ROUTE_2: {
          title: route2RequirementsTitle,
          items: [
            oneGroup2aDocument,
            t("2 additional documents"),
            t("An external identity validation check"),
          ],
        },
        ROUTE_3: {
          title: t("Route 3 requirements"),
          items: [
            t("A birth certificate"),
            t("Multiple supporting documents from Groups 2a and 2b"),
          ],
        },
      },
      areRouteRequirementsMet: standardEnhancedAreRouteRequirementsMet,
    };

    const basic: ManualIdvRouteRulesConfig = {
      routesIntro: t(
        "Use Route 1 wherever possible. Only use Route 2 if the applicant cannot provide a Group 1 document, and only after discussing with the applicant to confirm there's a valid reason they can't use Route 1. If the applicant cannot meet the Route 2 requirements either, this application cannot proceed as a Basic DBS check."
      ),
      availableRoutes: ["ROUTE_1", "ROUTE_2"],
      routeCopy: {
        ROUTE_1: {
          label: route1Label,
          description: t(
            "The applicant provides 1 document from Group 1 (such as a passport or driving licence) and 1 additional document from Group 1, 2a or 2b. The combination must confirm the applicant's name and date of birth. If it cannot, a third document may be added."
          ),
        },
        ROUTE_2: {
          label: t("Route 2 — Secondary documents"),
          description: t(
            "Used only if Route 1 cannot be satisfied. Requires 1 document from Group 2a, 2 further documents from Group 2a or 2b. The combination must confirm the applicant's name and date of birth."
          ),
        },
        ROUTE_3: standardRoute3Copy,
      },
      routeRequirementsInfo: {
        ROUTE_1: {
          title: route1RequirementsTitle,
          items: [
            oneGroup1Document,
            t("1 additional document from Group 1, 2a or 2b"),
            t(
              "The combination must confirm the applicant's name and date of birth. If it cannot, a third document may be added."
            ),
          ],
        },
        ROUTE_2: {
          title: route2RequirementsTitle,
          items: [
            oneGroup2aDocument,
            t("2 further documents from Group 2a or 2b"),
          ],
        },
      },
      areRouteRequirementsMet: basicAreRouteRequirementsMet,
    };

    return {
      basic,
      standard_enhanced: standardEnhanced,
    };
  }, [t]);
}
